import { useState, useEffect } from "react";
import { MapPin, Phone, Mail, Facebook, Instagram } from "lucide-react";
import axios from "axios";
import { useSiteImages } from "../context/SiteImagesContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const siteImages = useSiteImages();
  const [liveStatus, setLiveStatus] = useState([]);
  const [footerText, setFooterText] = useState({ tagline: "", copyright_text: "" });
  const [socialLinks, setSocialLinks] = useState({ facebook_url: "", instagram_url: "" });

  useEffect(() => {
    fetchLiveStatus();
    fetchFooterText();
    fetchSocialLinks();
    const interval = setInterval(fetchLiveStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchLiveStatus = async () => {
    try {
      const response = await axios.get(`${API}/opening-hours/status`);
      setLiveStatus(response.data);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const fetchFooterText = async () => {
    try {
      const response = await axios.get(`${API}/footer-text`);
      setFooterText(response.data);
    } catch (error) {
      console.error("Error fetching footer text:", error);
    }
  };

  const fetchSocialLinks = async () => {
    try {
      const response = await axios.get(`${API}/social-links`);
      setSocialLinks(response.data);
    } catch (error) {
      console.error("Error fetching social links:", error);
    }
  };

  const getStatus = (locationId) => {
    return liveStatus.find(s => s.location_id === locationId);
  };

  return (
    <footer id="contact">
      {/* Main footer */}
      <div className="bg-[#ff3131] text-white py-14">
        <div className="container-gymtots">
          
          {/* Top: Logo + Affiliations + Social */}
          <div className="text-center mb-10">
            <a href="/" className="inline-block mb-5">
              <img src={siteImages["gymtots-logo.png"] || ""} alt="Gymtots" className="h-12 w-auto brightness-0 invert mx-auto" />
            </a>
            <div className="flex items-center justify-center gap-3 mb-5">
              <div className="bg-[#1656a4] rounded-xl w-[88px] h-[44px] flex items-center justify-center p-1.5 overflow-hidden">
                <img src={siteImages["dynamo-logo-cropped.png"] || ""} alt="Dynamo Gymnastics" className="max-w-full max-h-full object-contain" />
              </div>
              <div className="bg-white rounded-xl w-[88px] h-[44px] flex items-center justify-center p-1.5">
                <img src={siteImages["british-gymnastics-logo.webp"] || ""} alt="British Gymnastics" className="max-h-full max-w-full object-contain" />
              </div>
            </div>
            <div className="flex gap-3 justify-center">
              {socialLinks.facebook_url && (
                <a href={socialLinks.facebook_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white hover:text-[#ff3131] transition-colors" aria-label="Facebook" data-testid="footer-facebook-link">
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              {socialLinks.instagram_url && (
                <a href={socialLinks.instagram_url} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center hover:bg-white hover:text-[#ff3131] transition-colors" aria-label="Instagram" data-testid="footer-instagram-link">
                  <Instagram className="w-4 h-4" />
                </a>
              )}
            </div>
          </div>

          {/* Middle: 3-column grid — Classes, Locations, Contact */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center max-w-4xl mx-auto mb-10">

            {/* Classes & Links */}
            <div>
              <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Our Classes</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="/classes" className="hover:text-white transition-colors">Under 2's</a></li>
                <li><a href="/classes" className="hover:text-white transition-colors">2 Years</a></li>
                <li><a href="/classes" className="hover:text-white transition-colors">3-4 Years</a></li>
                <li><a href="/classes" className="hover:text-white transition-colors">Under 4's</a></li>
                <li><a href="/classes" className="hover:text-white transition-colors">Gyminis</a></li>
              </ul>
              <h4 className="text-sm font-bold mt-6 mb-3" style={{ fontFamily: 'Fredoka, cursive' }}>Quick Links</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="/about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/birthday-parties" className="hover:text-white transition-colors">Birthday Parties</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms</a></li>
              </ul>
            </div>

            {/* Locations & Hours */}
            <div>
              <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Locations & Hours</h4>
              <div className="space-y-4">
                {[
                  { id: "hamble", name: "Gymtots Hamble" },
                  { id: "west_end", name: "Gymtots West End" },
                  { id: "head_office", name: "Head Office" }
                ].map(loc => {
                  const s = getStatus(loc.id);
                  return (
                    <div key={loc.id} className="flex flex-col items-center">
                      <div className="flex items-center gap-2 flex-wrap justify-center">
                        <MapPin className="w-3.5 h-3.5 text-white/70" />
                        <span className="text-sm font-semibold text-white">{loc.name}</span>
                        {s && (
                          <span data-testid={`status-${loc.id.replace('_', '-')}`} className={`text-[10px] leading-none px-2 py-1 rounded-full font-bold ${s.is_open_now ? 'bg-white/20 text-white' : 'bg-white/10 text-white/60'}`}>
                            {s.is_open_now ? 'Open' : 'Closed'}
                          </span>
                        )}
                      </div>
                      {s && s.today_hours && (
                        <p className="text-xs text-white/50 mt-0.5">Today: {s.today_hours}</p>
                      )}
                    </div>
                  );
                })}
              </div>
              <a href="/locations" className="inline-block mt-5 text-xs text-white/60 hover:text-white transition-colors underline underline-offset-2">
                View all locations & hours
              </a>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-sm font-bold mb-4" style={{ fontFamily: 'Fredoka, cursive' }}>Contact Us</h4>
              <div className="space-y-3 text-sm">
                <a href="tel:02380455007" className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors">
                  <Phone className="w-4 h-4 text-white flex-shrink-0" />
                  02380 455007
                </a>
                <a href="mailto:admin@gym-tots.co.uk" className="flex items-center justify-center gap-2 text-white/80 hover:text-white transition-colors">
                  <Mail className="w-4 h-4 text-white flex-shrink-0" />
                  admin@gym-tots.co.uk
                </a>
              </div>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                  <span><span className="font-semibold text-white">Hamble</span> <span className="text-white/70">· SO31 4NE</span></span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                  <span><span className="font-semibold text-white">West End</span> <span className="text-white/70">· SO30 3FE</span></span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <MapPin className="w-3.5 h-3.5 text-white/70 flex-shrink-0" />
                  <span><span className="font-semibold text-white">Head Office</span> <span className="text-white/70">· SO31 4NE</span></span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom bar */}
      <div className="bg-[#7dbff8] py-3">
        <div className="container-gymtots">
          <p className="text-white text-xs text-center" style={{ fontFamily: 'Nunito, sans-serif' }}>
            &copy; {new Date().getFullYear()} {footerText.copyright_text || "Gymtots · British Gymnastics Registered · All coaches DBS checked"}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
